import React from 'react';
import ErrorPage from 'next/error';
import { NextPageContext } from 'next';
import { isBugsnagActive } from '@elevator/ui';
import bugsnagClient from 'lib/errors/bugsnag';
import Error from 'layouts/Error';

/**
 * Custom Error Page (_error.tsx)
 *
 * Next.js special file that handles unhandled errors in the application.
 * This page is automatically called by Next.js in the following scenarios:
 *
 * 1. React Error Boundary Fallback:
 *    - When a React component throws an error during rendering
 *    - Used as FallbackComponent in _app.tsx ErrorBoundary
 *
 * 2. Server-Side Errors:
 *    - Errors in getStaticProps, getServerSideProps, or API routes
 *    - 500+ HTTP status code errors
 *    - Unhandled exceptions during page rendering
 *
 * Features:
 * - Error reporting to Bugsnag (if enabled)
 * - Status code handling (defaults to 500 for unhandled errors)
 * - Custom error display with GIPHY background images
 *
 * Note: This is different from 404.tsx, which handles "not found" errors.
 * This page handles actual application errors and exceptions.
 */
export default class Page extends React.Component<ErrorProps> {
  static async getInitialProps(ctx: NextPageContext) {
    // Report error to Bugsnag if enabled and error exists
    if (isBugsnagActive() && bugsnagClient && ctx.err) {
      bugsnagClient.notify(ctx.err);
    }
    // Get status code from Next.js ErrorPage
    return ErrorPage.getInitialProps(ctx);
  }

  render(): React.JSX.Element {
    // Default to 500 for unhandled errors if statusCode is not provided
    const statusCode = this.props.statusCode ?? 500;
    return <Error statusCode={statusCode} />;
  }
}

/**
 * Props for the Error page component
 */
export interface ErrorProps {
  /**
   * HTTP status code (e.g., 404, 500, etc.)
   * Defaults to 500 if not provided
   */
  statusCode?: number;
}
